import { useState } from "react";
import { Image, ImageType } from "src/business/restaurant/gallery/asset";
import { API_URL } from "src/Utils/API";
import { captureError } from "src/monitoring/sentry";

// ─── Batch sync (used by the Gallery page) ────────────────────────────────────

export interface UploadEntry {
  file: File;
  imageType: ImageType;
}

const uploadByType = (
  restaurantId: string,
  imageType: ImageType,
  entries: UploadEntry[]
): Promise<Response> => {
  const formData = new FormData();
  entries.forEach(({ file }) => formData.append("images[]", file));
  formData.append("imageType", imageType);
  return fetch(`${API_URL}/restaurant/${restaurantId}/gallery`, {
    method: "POST",
    body: formData,
    credentials: "include",
  });
};

const deleteImages = (
  restaurantId: string,
  uris: string[]
): Promise<Response> => {
  const formData = new FormData();
  uris.forEach((uri) => formData.append("uris[]", uri));
  return fetch(`${API_URL}/restaurant/${restaurantId}/gallery`, {
    method: "DELETE",
    body: formData,
    credentials: "include",
  });
};

const groupByImageType = (
  entries: UploadEntry[]
): Partial<Record<ImageType, UploadEntry[]>> =>
  entries.reduce<Partial<Record<ImageType, UploadEntry[]>>>((acc, entry) => {
    (acc[entry.imageType] ??= []).push(entry);
    return acc;
  }, {});

export const useSyncGallery = (restaurantId?: string) => {
  const [syncing, setSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [syncError, setSyncError] = useState(false);

  const syncGallery = async (toUpload: UploadEntry[], toDelete: string[]) => {
    if (!restaurantId) return;

    setSyncing(true);
    setSyncSuccess(false);
    setSyncError(false);

    const grouped = groupByImageType(toUpload);

    const calls: Promise<Response>[] = [
      ...Object.entries(grouped).map(([imageType, entries]) =>
        uploadByType(restaurantId, imageType as ImageType, entries!)
      ),
      ...(toDelete.length > 0 ? [deleteImages(restaurantId, toDelete)] : []),
    ];

    const results = await Promise.allSettled(calls);

    const failed = results.some(
      (r) =>
        r.status === "rejected" ||
        (r.status === "fulfilled" && !r.value.ok)
    );

    setSyncError(failed);
    setSyncSuccess(!failed);
    setSyncing(false);
  };

  const resetSyncStatus = () => {
    setSyncSuccess(false);
    setSyncError(false);
  };

  return { syncing, syncSuccess, syncError, syncGallery, resetSyncStatus };
};

export const useUploadImages = (
  restaurant_id?: string,
) => {
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(false);

  const uploadImages = async (images: Image[], imageType: ImageType,) => {
    if (!restaurant_id) return

    setUploading(true);
    setUploadSuccess(false);
    setUploadError(false);

    const formData = new FormData();
    images.forEach((image) => {
      if (image.file) {
        formData.append("images[]", image.file);
      }
    });

    formData.append("imageType", imageType);

    await fetch(`${API_URL}/restaurant/${restaurant_id}/gallery`, {
      method: "POST",
      body: formData,
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) {
          setUploadError(true);
          return;
        }
        setUploadSuccess(true);
      })
      .catch((error) => {
        captureError(error);
        setUploadError(true);
      })
      .finally(() => {
        setUploading(false);
      });
  };

  const resetUploadStatus = () => {
    setUploadSuccess(false);
    setUploadError(false);
  };

  return {
    uploading,
    uploadSuccess,
    uploadError,
    uploadImages,
    resetUploadStatus,
  };
};

export const useDeleteImage = (restaurantId: string) => {
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deleteError, setDeleteError] = useState(false);

  const deleteImage = async (imageId: string) => {
    await fetch(`${API_URL}/restaurant/${restaurantId}/gallery/${imageId}`, {
      method: "DELETE",
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) {
          setDeleteError(true);
          return;
        }
        setDeleteSuccess(true);
      })
      .catch((e) => {
        captureError(e);
        setDeleteError(true);
      });
  };

  return {
    deleteSuccess,
    deleteError,
    deleteImage,
  };
};
